import { useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import type { Editor } from "@tiptap/react";
import { MentionInput } from "../MentionInput";

interface CommentPopoverProps {
  editor: Editor;
  onAddComment: (params: {
    content: string;
    mentionedUids: string[];
    textRange: { from: number; to: number; text: string };
  }) => void;
}

export function CommentPopover({ editor, onAddComment }: CommentPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [comment, setComment] = useState("");

  const { from, to } = editor.state.selection;
  const hasSelection = from !== to;

  const handleSubmit = (mentionedUids: string[]) => {
    if (!comment.trim() || !hasSelection) return;

    const selectedText = editor.state.doc.textBetween(from, to, " ");

    onAddComment({
      content: comment.trim(),
      mentionedUids,
      textRange: { from, to, text: selectedText },
    });

    setComment("");
    setIsOpen(false);
  };

  if (!hasSelection) return null;

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1 rounded-md border border-border bg-popover px-2 py-1 text-[10px] font-medium text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground"
        title="Add comment on selection"
      >
        <MessageSquarePlus className="h-3 w-3" />
        Comment
      </button>
    );
  }

  return (
    <div className="w-64 rounded-lg border border-border bg-popover p-3 shadow-lg">
      <div className="mb-2 text-[10px] font-medium text-muted-foreground">
        Comment on selection
      </div>
      <div className="mb-2 rounded border border-border bg-muted/50 px-2 py-1 text-[10px] italic text-muted-foreground line-clamp-2">
        &ldquo;{editor.state.doc.textBetween(from, to, " ")}&rdquo;
      </div>
      <MentionInput
        value={comment}
        onChange={setComment}
        onSubmit={handleSubmit}
        placeholder="Add your comment..."
        autoFocus
        className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[11px] text-foreground placeholder:text-muted-foreground"
      />
      <div className="mt-2 flex justify-end gap-1.5">
        <button
          onClick={() => {
            setIsOpen(false);
            setComment("");
          }}
          className="rounded px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          onClick={() => handleSubmit([])}
          disabled={!comment.trim()}
          className="rounded bg-primary px-2.5 py-1 text-[10px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          Comment
        </button>
      </div>
    </div>
  );
}
